/**
 * Earnings Tab - Historical earnings data and beat/miss statistics
 */
import { useEarningsHistory, useEarningsSummary, useRefreshEarningsHistory } from '../../hooks/useEarnings'
import { LoadingCard, Button } from '../common'
import {
  getSurpriseColor,
  formatSurprise,
  getStreakLabel,
  getStreakColor,
} from '../../types/earnings'

interface EarningsTabProps {
  symbol: string
}

export function EarningsTab({ symbol }: EarningsTabProps) {
  const { data: history, isLoading: historyLoading } = useEarningsHistory(symbol)
  const { data: summaryResponse, isLoading: summaryLoading } = useEarningsSummary(symbol)
  const refreshMutation = useRefreshEarningsHistory()

  const isLoading = historyLoading || summaryLoading
  const summary = summaryResponse?.summary

  const handleRefresh = () => {
    refreshMutation.mutate(symbol)
  }

  if (isLoading) {
    return <LoadingCard />
  }

  const entries = history?.history || []

  // Format date
  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  // Format EPS
  const formatEps = (eps?: number) => {
    if (eps === undefined || eps === null) return '-'
    return `$${eps.toFixed(2)}`
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-xs font-medium text-gray-500 uppercase">Beat Rate</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900">
            {summary?.beat_rate_pct !== undefined ? `${summary.beat_rate_pct.toFixed(0)}%` : '-'}
          </div>
          <div className="mt-1 text-sm text-gray-500">
            {summary?.beat_count ?? 0} beats / {summary?.miss_count ?? 0} misses
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-xs font-medium text-gray-500 uppercase">Avg Surprise</div>
          <div className={`mt-1 text-2xl font-semibold ${getSurpriseColor(summary?.avg_surprise_pct)}`}>
            {formatSurprise(summary?.avg_surprise_pct)}
          </div>
          <div className="mt-1 text-sm text-gray-500">
            of {summary?.total_quarters ?? 0} quarters
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-xs font-medium text-gray-500 uppercase">Current Streak</div>
          <div className={`mt-1 text-2xl font-semibold ${getStreakColor(summary?.streak ?? 0)}`}>
            {getStreakLabel(summary?.streak ?? 0)}
          </div>
          <div className="mt-1 text-sm text-gray-500">
            consecutive quarters
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-4">
          <div className="text-xs font-medium text-gray-500 uppercase">Next Earnings</div>
          <div className="mt-1 text-2xl font-semibold text-gray-900">
            {summary?.next_earnings_date
              ? formatDate(summary.next_earnings_date)
              : 'Unknown'}
          </div>
          {summary?.days_until_earnings !== undefined && (
            <div className="mt-1 text-sm text-gray-500">
              {summary.days_until_earnings === 0
                ? 'Today'
                : summary.days_until_earnings === 1
                ? 'Tomorrow'
                : `In ${summary.days_until_earnings} days`}
            </div>
          )}
        </div>
      </div>

      {/* Refresh Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleRefresh}
          disabled={refreshMutation.isPending}
          variant="secondary"
          size="sm"
        >
          <ArrowPathIcon className={`w-4 h-4 mr-2 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
          Refresh History
        </Button>
      </div>

      {/* History Table */}
      {entries.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <p className="text-gray-500">
            No earnings history available for {symbol}.
          </p>
          <p className="text-sm text-gray-400 mt-2">
            Click "Refresh History" to fetch data from Alpha Vantage.
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Quarter
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Report Date
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Estimated EPS
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Reported EPS
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Surprise
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Price Reaction
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.map((entry, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(entry.fiscal_date_ending)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(entry.reported_date)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                    {formatEps(entry.estimated_eps)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                    {formatEps(entry.reported_eps)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-medium ${getSurpriseColor(entry.surprise_pct)}`}>
                    {formatSurprise(entry.surprise_pct)}
                  </td>
                  <td className={`px-6 py-4 whitespace-nowrap text-sm text-right ${
                    entry.price_change_pct !== undefined && entry.price_change_pct !== null
                      ? entry.price_change_pct >= 0
                        ? 'text-green-600'
                        : 'text-red-600'
                      : 'text-gray-500'
                  }`}>
                    {entry.price_change_pct !== undefined && entry.price_change_pct !== null
                      ? `${entry.price_change_pct >= 0 ? '+' : ''}${entry.price_change_pct.toFixed(1)}%`
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Refresh status messages */}
      {refreshMutation.isSuccess && (
        <div className="text-sm text-green-600">
          History refreshed successfully
        </div>
      )}
      {refreshMutation.isError && (
        <div className="text-sm text-red-600">
          Failed to refresh: {(refreshMutation.error as Error).message}
        </div>
      )}
    </div>
  )
}

function ArrowPathIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  )
}
